// src/pages/AddIncentivePlan.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleCustomResponse } from "../utils/response";
import { staffApi } from "../Apis/Api";
import { apiCall } from "../Apis/apiCall";

const validationSchema = Yup.object().shape({
  plan_name: Yup.string().required("Plan name is required"),
  plan_type: Yup.string().required("Plan type is required"),
  period_type: Yup.object()
    .shape({
      value: Yup.string().required("Period type is required"),
      label: Yup.string(),
    })
    .required("Period type is required"),
  value_type: Yup.object()
    .shape({
      value: Yup.string().required("Value type is required"),
      label: Yup.string(),
    })
    .required("Value type is required"),
  fixed_value: Yup.number()
    .typeError("Incentive value must be a number")
    .required("Incentive value is required")
    .min(0, "Incentive value must be greater than or equal to 0")
    .test(
      "maxDigits",
      "Incentive value cannot have more than 2 decimal places",
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  currency_code: Yup.string().when("value_type", {
    is: (value_type) => value_type?.value === "currency",
    then: (schema) => schema.required("Currency code is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AddIncentivePlan = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");

  // Get user currency from localStorage
  useEffect(() => {
    const getUserCurrency = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          return user.currency_code || "";
        }
      } catch (error) {
        console.error("Error getting user currency from localStorage:", error);
      }
      return "";
    };

    setDefaultCurrency(getUserCurrency());
  }, []);

  // Period type options
  const periodTypeOptions = [
    { label: "Daily", value: "daily" },
    { label: "Monthly", value: "monthly" },
    { label: "Weekly", value: "weekly" },
  ];

  // Value type options
  const valueTypeOptions = [
    { label: "Currency", value: "currency" },
    { label: "Points", value: "points" },
  ];

  // Fetch currency codes
  useEffect(() => {
    const fetchCurrencyCodes = async () => {
      try {
        const res = await apiCall("get", "/get-currency-codes?want_pagination=0");
        if (res.data.success) {
          const uniqueCurrencies = Array.from(
            new Map(res.data.data.map((currency) => [currency.currency_code, currency])).values()
          );
          const options = uniqueCurrencies.map((currency) => ({
            label: `${currency.currency_code} (${currency.currency_symbol})`,
            value: currency.currency_code,
          }));
          setCurrencyOptions(options);
        } else {
          console.error("Failed to fetch currency codes:", res.message);
          handleCustomResponse("error", res.message || "Failed to fetch currency codes!");
        }
      } catch (err) {
        console.error("Error fetching currency codes:", err);
        handleCustomResponse("error", "Error fetching currency codes!");
      }
    };

    fetchCurrencyCodes();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      plan_name: "",
      plan_type: "location_based",
      period_type: null,
      value_type: null,
      fixed_value: "",
      currency_code: defaultCurrency,
    },
  });

  // Set default currency when it's available
  useEffect(() => {
    if (defaultCurrency) {
      setValue("currency_code", defaultCurrency);
    }
  }, [defaultCurrency, setValue]);

  const selectedValueType = watch("value_type");

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const payload = {
        plan_name: data.plan_name,
        plan_type: data.plan_type,
        period_type: data.period_type.value,
        value_type: data.value_type.value,
        fixed_value: parseFloat(data.fixed_value),
        currency_code: data.value_type.value === "currency" ? data.currency_code.toUpperCase() : undefined,
      };

      console.log("Form Data:", payload);
      
      const response = await staffApi.createIncentivePlan(payload);
      
      console.log("API Response:", response);
      
      if (response.success) {
        handleCustomResponse("success", response.message || "Plan created successfully!");
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        handleCustomResponse("error", response.message || "Failed to create plan!");
      }
      
    } catch (err) {
      console.error("Error creating incentive plan:", err);
      let errorMessage = "Failed to create plan!";
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = err.error;
      }
      
      handleCustomResponse("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb title="Add Plan" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="row g-20">
              <div className="col-md-6 mb-20">
                <label htmlFor="plan_name" className="form-label">
                  Plan Name *
                </label>
                <input
                  id="plan_name"
                  type="text"
                  {...register("plan_name")}
                  className="form-control radius-8"
                  placeholder="Enter Plan Name"
                />
                {errors.plan_name && (
                  <span className="text-danger-600">
                    {errors.plan_name.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Period Type *</label>
                <Controller
                  name="period_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="period_type"
                      value={field.value}
                      onChange={field.onChange}
                      options={periodTypeOptions}
                      placeholder="Select Period Type"
                      isClearable
                      noOptionsMessage={() => (
                        <div>
                          <p>No period types available</p>
                        </div>
                      )}
                    />
                  )}
                />
                {errors.period_type && (
                  <span className="text-danger-600">
                    {errors.period_type.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Value Type *</label>
                <Controller
                  name="value_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="value_type"
                      value={field.value}
                      onChange={field.onChange}
                      options={valueTypeOptions}
                      placeholder="Select Value Type"
                      isClearable
                      noOptionsMessage={() => (
                        <div>
                          <p>No value types available</p>
                        </div>
                      )}
                    />
                  )}
                />
                {errors.value_type && (
                  <span className="text-danger-600">
                    {errors.value_type.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="fixed_value" className="form-label">
                  Incentive Value *
                </label>
                <input
                  id="fixed_value"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register("fixed_value")}
                  className="form-control radius-8"
                  placeholder="Enter Incentive Value"
                />
                {errors.fixed_value && (
                  <span className="text-danger-600">
                    {errors.fixed_value.message}
                  </span>
                )}
              </div>

              {selectedValueType?.value === "currency" && (
                <div className="col-md-6 mb-20">
                  <label htmlFor="currency_code" className="form-label">
                    Currency Code *
                  </label>
                  <input
                    id="currency_code"
                    type="text"
                    {...register("currency_code")}
                    className="form-control radius-8"
                    placeholder="Currency Code"
                    value={defaultCurrency}
                    readOnly
                    disabled
                  />
                  <small className="">
                    Using your default currency: {defaultCurrency}
                  </small>
                  {errors.currency_code && (
                    <span className="text-danger-600">{errors.currency_code.message}</span>
                  )}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm disabled={isLoading} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddIncentivePlan;